import React, { useLayoutEffect } from 'react';
import { Route, Switch, useLocation } from 'react-router-dom';
import ShowLoading from './Components/loading/ShowLoading';
import { LiffPrivateRoute } from '@Components/';
import { Icons, IconButton } from '@curves/ui-base';
import { SnackbarProvider, closeSnackbar } from 'notistack';
import { AUTH_ROOT_ROUTER, LOGIN_ROOT_ROUTER, LOGIN_CAPTCHA_ROOT_ROUTER, COMING_SOON_ROOT_ROUTER } from '@Router/routerPath';
import AppVersion from './AppVersion';
import { LiffLoginPage, LoginPage, AuthPage } from '@Local/Login/';
import ComingSoonPage from '@Local/ComingSoon/ComingSoonPage';
import { localUser } from '@Util/utils';
import { parseDate } from '@Util/moment';
import Home from '@Local/Home/';
import ErrorBoundary from '@Local/Components/ErrorBoundary/ErrorBoundary';
import '@Scss/site.scss';

const DAILY_REFRESH_QUERY_KEY = '_dailyRef';

/**
 * 取得今日日期字串（YYYY-MM-DD）。
 * 使用專案既有的 moment 包裝，避免 String.padStart（ES2017）在舊瀏覽器不存在。
 * @returns {String} ex: 2026-08-13
 */
const getTodayKey = () => parseDate();

/**
 * 組出帶有每日更新註記的重整網址。
 * 只使用 URLSearchParams（App.js 既有用法），不引入相容性較差的 new URL()。
 * @param {String} todayKey 今日日期字串
 * @returns {String} 完整網址
 */
const buildRefreshUrl = (todayKey) => {
    const { protocol, host, pathname, search, hash } = window.location;
    const params = new URLSearchParams(search);

    params.set(DAILY_REFRESH_QUERY_KEY, todayKey);

    const queryString = params.toString();

    return `${protocol}//${host}${pathname}${queryString ? `?${queryString}` : ''}${hash}`;
};

/**
 * 判斷頁面是否為可見狀態。
 * 舊版 Android WebView 僅提供帶前綴的 webkitVisibilityState；兩者皆讀不到時視為可見。
 * @returns {Boolean}
 */
const isPageVisible = () => {
    const visibilityState = document.visibilityState || document.webkitVisibilityState;

    return !visibilityState || visibilityState === 'visible';
};

// LIFF 進入點深連結（liff.state / 根路徑 ?t=）的還原已前移至 index.js normalizeLiffEntry，
// 在 Router 掛載前同步完成，故此處不再處理 liff.state，避免與 PrivateRoute 導頁競態。
const App = React.forwardRef((props, ref) => {
    const onClickDismiss = (snackbarId) => () => {
        closeSnackbar(snackbarId);
    };

    const dailyRefreshRef = React.useRef(false);
    const { pathname } = useLocation();

    useLayoutEffect(() => {
        const checkDailyRefresh = () => {
            // 導向中不重複觸發（focus / visibilitychange / pageshow 可能連續觸發）
            if (dailyRefreshRef.current) return;

            try {
                const today = getTodayKey();
                const lastRefreshDate = localUser.getDayKey();

                // 同一天或首次進站：僅記錄日期，不需重整
                if (!lastRefreshDate || lastRefreshDate === today) {
                    localUser.setDayKey(today);
                    return;
                }

                // 先把網址組好，避免組網址失敗時留下「已登出卻沒重整」的狀態
                const refreshUrl = buildRefreshUrl(today);

                localUser.setDayKey(today);
                localUser.logout();
                dailyRefreshRef.current = true;
                window.location.replace(refreshUrl);
            } catch (error) {
                console.error('檢查每日更新時間發生錯誤:', error);
            }
        };

        const handleVisibilityChange = () => {
            if (isPageVisible()) checkDailyRefresh();
        };

        checkDailyRefresh();
        document.addEventListener('visibilitychange', handleVisibilityChange);
        document.addEventListener('webkitvisibilitychange', handleVisibilityChange);
        window.addEventListener('focus', checkDailyRefresh);
        window.addEventListener('pageshow', checkDailyRefresh);

        return () => {
            document.removeEventListener('visibilitychange', handleVisibilityChange);
            document.removeEventListener('webkitvisibilitychange', handleVisibilityChange);
            window.removeEventListener('focus', checkDailyRefresh);
            window.removeEventListener('pageshow', checkDailyRefresh);
        };
    }, []);

    return (
        <React.Fragment>
            <SnackbarProvider
                className="snacker-massage"
                maxSnack={3}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'right',
                }}
                action={(snackbarId) => (
                    <IconButton onClick={onClickDismiss(snackbarId)}>
                        <Icons.Close htmlColor="#fff" />
                    </IconButton>
                )}
            >
                <AppVersion />
                {/* 全站最後一道防線：避免任何未捕捉的渲染錯誤造成整站白畫面 */}
                <ErrorBoundary resetKey={pathname}>
                    <Switch>
                        <Route exact path={AUTH_ROOT_ROUTER} component={AuthPage} />
                        <Route exact path={LOGIN_ROOT_ROUTER} component={LiffLoginPage} />
                        <Route exact path={LOGIN_CAPTCHA_ROOT_ROUTER} component={LoginPage} />
                        {/* 刻意免登入：未綁定會員的好友也要看得到 */}
                        <Route exact path={COMING_SOON_ROOT_ROUTER} component={ComingSoonPage} />
                        <LiffPrivateRoute as={Home} />
                    </Switch>
                </ErrorBoundary>
            </SnackbarProvider>
            <ShowLoading />
        </React.Fragment>
    );
});

export default App;
